import React, { useState } from 'react';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { OtherTestForm } from './OtherTestForm';
import { PAGE_TEXT } from '../lang';
import {
  useMoveApplicationItem,
  type OtherTestFormData,
} from '../../../graphql';
import {
  Accordion,
  Alert,
  type AlertDetails,
  IconButton,
  NotificationText,
} from '../../../../../components';
import {
  type FormController,
  FormModificationMode,
} from '../../../../../utils';

type OtherTestProps = {
  applicationNo: string;
  otherTests: OtherTestFormData[];
  enableEdit: FormController;
  isConditionEditable: boolean;
  enableDelete: boolean;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const OtherTest: React.FunctionComponent<OtherTestProps> = props => {
  const {
    QUALIFICATION: { OTHER_TEST },
  } = PAGE_TEXT;
  const {
    applicationNo = '',
    otherTests = [],
    enableEdit,
    isConditionEditable,
    refetchApplication,
    handleLoading,
    enableDelete,
  } = props;
  const { applicationId } = useParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });
  const [keyIds, setKeyIds] = useState<string[]>(
    otherTests.map(() => uuidv4()),
  );

  function addOtherTest(): void {
    setKeyIds(() => [...keyIds, uuidv4()]);
  }

  const removeApplicationItem = useMoveApplicationItem(refetchApplication);

  const handleDelete = async (): Promise<void> => {
    // just need to delete form in DOM
    if (otherTests.length === 0) {
      setKeyIds([]);
      setOpenAlert(true);
      setAlertDetails({
        alertContent: 'Deleted Other Test Successfully.',
        alertType: 'success',
      });
      return;
    }

    await handleLoading(async () => {
      try {
        const id = otherTests?.[0]?.otherTestId;
        const variables = {
          id: applicationId,
          input: {
            otherTestId: id,
          },
        };
        await removeApplicationItem({ variables });
        setKeyIds([]);
        refetchApplication();
        setAlertDetails({
          alertContent: 'Deleted Other Test Successfully.',
          alertType: 'success',
        });
      } catch (e) {
        setAlertDetails({
          alertContent: 'Deleted Other Test Failed.' + (e as Error)?.message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const note = `Please refer to the <a
    href = "https://www.polyu.edu.hk/study/pg/research-postgraduate/supporting-documents-application-rpg"
    target="_blank">specific programme requirements</a> set by the individual departments.`;

  const displayAddButton =
    (enableEdit.formModificationMode === FormModificationMode.ENABLE ||
      enableEdit.formModificationMode ===
        FormModificationMode.CONDITION_MEET) &&
    keyIds.length === 0;

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <NotificationText note={note} />
      {displayAddButton ? (
        <div className="add-button">
          <IconButton icon={<AddIcon />} onClick={addOtherTest} />
          {PAGE_TEXT.QUALIFICATION.OTHER_TEST.ADD_MORE_FORM}
        </div>
      ) : (
        <ContentBox>
          {keyIds.map((keyId, index) => {
            const details = (
              <OtherTestForm
                key={keyId}
                applicationNo={applicationNo}
                initialValues={
                  index > otherTests.length ? undefined : otherTests[index]
                }
                enableEdit={enableEdit}
                isConditionEditable={isConditionEditable}
                enableDelete={enableDelete}
                refetchApplication={refetchApplication}
                handleDelete={handleDelete}
                handleLoading={handleLoading}
              />
            );
            return (
              <Accordion
                style={{ width: '100%' }}
                key={keyId}
                title={OTHER_TEST.TITLE}
                defaultExpanded
                details={details}
              />
            );
          })}
        </ContentBox>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;

  .MuiGrid-item {
    width: 100%;
  }

  .add-button {
    margin-top: 1rem;
    margin-bottom: 2rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;

    .MuiSvgIcon-root {
      background-color: #951d1d;
      color: #fff;
      border-radius: 50%;
      cursor: pointer;
    }

    button {
      margin-right: 1rem;
    }
  }
`;

const ContentBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1rem;
`;
